import { useState } from "react";
import { ChevronDown, AlertTriangle } from "lucide-react";
import { fileUrl, formatPrice } from "@/lib/api";
import { CompatBadge, Stars } from "@/components/common/Badges";

const DOT = { verified: "bg-emerald-400", possible: "bg-yellow-400", incompatible: "bg-red-500" };

export function Checks({ checks = [], warnings = [] }) {
  return (
    <ul className="space-y-1 text-xs">
      {checks.map((c) => (
        <li key={c.rule} className="flex gap-2"><span className={`mt-1 h-2 w-2 shrink-0 rounded-full ${DOT[c.status]}`} /><span><span className="text-zinc-500">{c.description}:</span> {c.message}</span></li>
      ))}
      {warnings.map((w) => <li key={w} className="flex gap-2 text-yellow-700"><AlertTriangle className="h-3 w-3 shrink-0 mt-0.5" />{w}</li>)}
    </ul>
  );
}

function Candidate({ c, selected, onSelect }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`panel p-4 ${selected ? "border-[#0071e3]" : ""}`} data-testid={`candidate-${c.id}`}>
      <div className="flex gap-3">
        {c.image && <img src={fileUrl(c.image)} alt={c.title || "Parça görseli"} loading="lazy" decoding="async" className="h-14 w-14 rounded object-cover" />}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="font-medium">{c.title}</p>
            {c.type === "listing" && <span className="rounded bg-yellow-500/20 px-1.5 text-[10px] text-yellow-700">Katalog dışı · satıcı verisi</span>}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-zinc-500">
            <CompatBadge status={c.status} testId={`candidate-status-${c.id}`} />
            {c.rating && <span className="flex items-center gap-1"><Stars value={c.rating.average || 0} size={3} />{c.rating.count}</span>}
            {c.active_listings > 0 && <span>{c.active_listings} ilan{c.min_price_try ? ` · ${formatPrice(c.min_price_try)}'den` : ""}</span>}
            {c.price && <span>{formatPrice(c.price, c.currency)}</span>}
          </div>
        </div>
        <button onClick={() => onSelect(c)} data-testid={`select-candidate-${c.id}`}
          className={`h-fit rounded-full px-4 py-1.5 text-sm ${selected ? "bg-[#0071e3] text-white" : "border border-black/[0.08] hover:border-[#0071e3]"}`}>{selected ? "Seçildi" : "Seç"}</button>
      </div>
      {(c.checks.length > 0 || c.warnings.length > 0) && (
        <button onClick={() => setOpen(!open)} className="mt-2 flex items-center gap-1 text-xs text-zinc-500" data-testid={`candidate-details-${c.id}`}>
          Uyumluluk ayrıntıları <ChevronDown className={`h-3 w-3 ${open ? "rotate-180" : ""}`} />
        </button>
      )}
      {open && <div className="mt-2"><Checks checks={c.checks} warnings={c.warnings} /></div>}
    </div>
  );
}

export default function CandidateList({ items, selectedId, onSelect, hideIncompatible }) {
  const shown = hideIncompatible ? items.filter((c) => c.status !== "incompatible") : items;
  if (!shown.length) return <p className="panel p-6 text-sm text-zinc-500" data-testid="no-candidates">Bu adım için uygun parça bulunamadı.</p>;
  return <div className="space-y-2" data-testid="candidate-list">{shown.map((c) => <Candidate key={c.type + c.id} c={c} selected={selectedId === c.id} onSelect={onSelect} />)}</div>;
}
